import { api, getToken } from '../../core/api-client';
import type {
  EmployeeWithUser, InviteEmployeeInput, UpdateEmployeeInput,
  AttendanceSession, DailyAttendanceSummary,
  LeaveRequest, LeaveStatus, CreateLeaveRequestInput,
  PenaltyRule, PenaltyRuleInput,
  LeaveTypeRow, LeaveTypeInput, LeaveBalance,
  CreateManualSessionInput, UpdateAttendanceSessionInput, CreateLeaveOnBehalfInput,
  AttendanceSettings, AttendanceSettingsInput,
  EmployeeDocument, EmployeeDocumentCategory, ExpiringEmployeeDocument, OrgChartNode,
  LinkedEsignDocument,
  ShiftTemplate, ShiftTemplateInput,
  ShiftAssignment, CreateShiftAssignmentInput,
  ShiftOverride, UpsertShiftOverrideInput,
  WeekSchedule, MySchedule,
  PerformanceGoal, CreateGoalInput, UpdateGoalInput,
  PerformanceReview, CreateReviewInput, PatchReviewInput,
} from './types';

export interface HrImportSummary {
  created: number;
  skipped: { row: number; reason: string }[];
}

export const hrApi = {
  // Employees
  listEmployees: () =>
    api<{ employees: EmployeeWithUser[] }>('/api/hr/employees').then((r) => r.employees),
  getEmployee: (id: string) =>
    api<{ employee: EmployeeWithUser }>(`/api/hr/employees/${id}`).then((r) => r.employee),
  inviteEmployee: (input: InviteEmployeeInput) =>
    api<{ employee: EmployeeWithUser; magic_link_token: string }>('/api/hr/employees', { method: 'POST', body: input }),
  updateEmployee: (id: string, patch: UpdateEmployeeInput) =>
    api<{ employee: EmployeeWithUser }>(`/api/hr/employees/${id}`, { method: 'PATCH', body: patch }).then((r) => r.employee),
  importEmployees: (rows: Record<string, string>[]) =>
    api<HrImportSummary>('/api/hr/employees/import', { method: 'POST', body: { rows } }),

  // Attendance
  myOpenSession: () =>
    api<{ session: AttendanceSession | null }>('/api/hr/attendance/me/open').then((r) => r.session),
  clockInSelf: (notes?: string) =>
    api<{ session: AttendanceSession }>('/api/hr/attendance/me/clock-in', { method: 'POST', body: { notes } }).then((r) => r.session),
  clockOutSelf: (notes?: string) =>
    api<{ session: AttendanceSession }>('/api/hr/attendance/me/clock-out', { method: 'POST', body: { notes } }).then((r) => r.session),
  listAttendance: (opts: { employeeId?: string; from?: string; to?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.employeeId) qs.set('employee_id', opts.employeeId);
    if (opts.from) qs.set('from', opts.from);
    if (opts.to) qs.set('to', opts.to);
    return api<{ rows: AttendanceSession[] }>(`/api/hr/attendance${qs.toString() ? '?' + qs : ''}`).then((r) => r.rows);
  },
  summary: (employeeId: string, from: string, to: string) =>
    api<{ summary: DailyAttendanceSummary[] }>(`/api/hr/attendance/summary?employee_id=${employeeId}&from=${from}&to=${to}`).then((r) => r.summary),
  // Self history — for users holding only hr.attendance.view_self
  myAttendance: (opts: { from?: string; to?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.from) qs.set('from', opts.from);
    if (opts.to) qs.set('to', opts.to);
    return api<{ rows: AttendanceSession[] }>(`/api/hr/attendance/me${qs.toString() ? '?' + qs : ''}`).then((r) => r.rows);
  },
  mySummary: (from: string, to: string) =>
    api<{ summary: DailyAttendanceSummary[] }>(`/api/hr/attendance/me/summary?from=${from}&to=${to}`).then((r) => r.summary),
  // HR-on-behalf manual attendance entry
  createManualSession: (input: CreateManualSessionInput) =>
    api<{ session: AttendanceSession }>('/api/hr/attendance', { method: 'POST', body: input }).then((r) => r.session),
  /**
   * Correct an attendance record. Send `clock_out_at: null` to deliberately
   * RE-OPEN a session; omit a field to leave it untouched. Without this a
   * forgotten clock-out blocks every later clock-in for that employee.
   */
  updateAttendanceSession: (id: string, patch: UpdateAttendanceSessionInput) =>
    api<{ session: AttendanceSession }>(`/api/hr/attendance/${id}`, { method: 'PATCH', body: patch }).then((r) => r.session),
  deleteAttendanceSession: (id: string) =>
    api<void>(`/api/hr/attendance/${id}`, { method: 'DELETE' }),

  // Attendance policy settings
  getAttendanceSettings: () =>
    api<{ settings: AttendanceSettings }>('/api/hr/attendance/settings').then((r) => r.settings),
  updateAttendanceSettings: (patch: AttendanceSettingsInput) =>
    api<{ settings: AttendanceSettings }>('/api/hr/attendance/settings', { method: 'PUT', body: patch }).then((r) => r.settings),

  // Leave
  myLeave: () =>
    api<{ rows: LeaveRequest[] }>('/api/hr/leave/me').then((r) => r.rows),
  submitLeave: (input: CreateLeaveRequestInput) =>
    api<{ request: LeaveRequest }>('/api/hr/leave/me', { method: 'POST', body: input }).then((r) => r.request),
  cancelLeave: (id: string) =>
    api<{ request: LeaveRequest }>(`/api/hr/leave/me/${id}/cancel`, { method: 'POST', body: {} }).then((r) => r.request),
  listLeave: (opts: { status?: LeaveStatus; employeeId?: string; from?: string; to?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.status) qs.set('status', opts.status);
    if (opts.employeeId) qs.set('employee_id', opts.employeeId);
    if (opts.from) qs.set('from', opts.from);
    if (opts.to) qs.set('to', opts.to);
    return api<{ rows: LeaveRequest[] }>(`/api/hr/leave${qs.toString() ? '?' + qs : ''}`).then((r) => r.rows);
  },
  // HR-on-behalf leave request
  submitLeaveForEmployee: (input: CreateLeaveOnBehalfInput) =>
    api<{ request: LeaveRequest }>('/api/hr/leave', { method: 'POST', body: input }).then((r) => r.request),
  reviewLeave: (id: string, status: 'approved' | 'denied', reviewer_note?: string) =>
    api<{ request: LeaveRequest }>(`/api/hr/leave/${id}/review`, { method: 'POST', body: { status, reviewer_note } }).then((r) => r.request),

  // Leave types
  listLeaveTypes: (activeOnly = false) =>
    api<{ types: LeaveTypeRow[] }>(`/api/hr/leave-types${activeOnly ? '?active_only=1' : ''}`).then((r) => r.types),
  createLeaveType: (input: LeaveTypeInput) =>
    api<{ type: LeaveTypeRow }>('/api/hr/leave-types', { method: 'POST', body: input }).then((r) => r.type),
  updateLeaveType: (id: string, patch: Partial<LeaveTypeInput>) =>
    api<{ type: LeaveTypeRow }>(`/api/hr/leave-types/${id}`, { method: 'PATCH', body: patch }).then((r) => r.type),
  deleteLeaveType: (id: string) =>
    api<{ ok: true }>(`/api/hr/leave-types/${id}`, { method: 'DELETE' }),

  // Leave balances
  employeeBalances: (employeeId: string, year?: number) =>
    api<{ balances: LeaveBalance[] }>(`/api/hr/employees/${employeeId}/leave-balances${year ? `?year=${year}` : ''}`).then((r) => r.balances),

  // Org chart
  orgChart: () =>
    api<{ nodes: OrgChartNode[] }>('/api/hr/org-chart').then((r) => r.nodes),

  // Employee documents
  listEmployeeDocuments: (employeeId: string) =>
    api<{ documents: EmployeeDocument[] }>(`/api/hr/employees/${employeeId}/documents`).then((r) => r.documents),
  myDocuments: () =>
    api<{ documents: EmployeeDocument[] }>('/api/hr/employees/me/documents').then((r) => r.documents),
  expiringDocuments: (days = 30) =>
    api<{ documents: ExpiringEmployeeDocument[] }>(`/api/hr/documents/expiring?days=${days}`).then((r) => r.documents),
  /**
   * E-sign envelopes linked to this employee — both the ones they signed and
   * the ones filed against their record. Gated server-side by
   * `hr.employees.view`, the same permission as the employee record itself.
   */
  listEmployeeEsignDocuments: (employeeId: string) =>
    api<{ documents: LinkedEsignDocument[] }>(`/api/hr/employees/${employeeId}/esign-documents`)
      .then((r) => r.documents),

  /**
   * Fetch a linked agreement's PDF as an authed blob and return an object URL.
   * Mirrors esignApi.downloadPdf exactly — the server proxies the bytes, so no
   * blob url ever reaches the browser, and a signed-but-unsealed document 409s
   * with a message explaining why (pass `original: true` to get the unsigned
   * source out of that state).
   */
  downloadEmployeeEsignPdf: async (
    employeeId: string, docId: string, opts: { original?: boolean } = {},
  ): Promise<string> => {
    const token = getToken();
    const res = await fetch(
      `/api/hr/employees/${employeeId}/esign-documents/${docId}/download${opts.original ? '?original=1' : ''}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) {
      const detail = await res.json().then(
        (b: any) => b?.error?.message as string | undefined,
        () => undefined,
      );
      throw new Error(detail ?? `Download failed (${res.status})`);
    }
    return URL.createObjectURL(await res.blob());
  },

  deleteEmployeeDocument: (employeeId: string, docId: string) =>
    api<void>(`/api/hr/employees/${employeeId}/documents/${docId}`, { method: 'DELETE' }),
  // Multipart upload — hand-rolled fetch, same pattern as expenses.uploadAttachment.
  uploadEmployeeDocument: async (
    employeeId: string,
    file: File,
    meta: { name: string; category: EmployeeDocumentCategory; expires_at?: string | null },
  ): Promise<EmployeeDocument> => {
    const token = getToken();
    const form = new FormData();
    form.append('file', file);
    form.append('name', meta.name);
    form.append('category', meta.category);
    if (meta.expires_at) form.append('expires_at', meta.expires_at);
    const res = await fetch(`/api/hr/employees/${employeeId}/documents`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const text = await res.text();
    let json: any = null;
    if (text) { try { json = JSON.parse(text); } catch { /* leave null */ } }
    if (!res.ok) {
      const msg = json?.error?.message ?? `Upload failed (${res.status})`;
      throw new Error(msg);
    }
    return json.document as EmployeeDocument;
  },

  // Penalty rules
  listPenaltyRules: () =>
    api<{ rules: PenaltyRule[] }>('/api/hr/penalty-rules').then((r) => r.rules),
  createPenaltyRule: (input: PenaltyRuleInput) =>
    api<{ rule: PenaltyRule }>('/api/hr/penalty-rules', { method: 'POST', body: input }).then((r) => r.rule),
  updatePenaltyRule: (id: string, patch: Partial<PenaltyRuleInput>) =>
    api<{ rule: PenaltyRule }>(`/api/hr/penalty-rules/${id}`, { method: 'PATCH', body: patch }).then((r) => r.rule),
  deletePenaltyRule: (id: string) =>
    api<{ ok: true }>(`/api/hr/penalty-rules/${id}`, { method: 'DELETE' }),

  // Shift scheduling (Phase 1) — templates
  listShiftTemplates: (activeOnly = false) =>
    api<{ templates: ShiftTemplate[] }>(`/api/hr/schedule/templates${activeOnly ? '?active_only=1' : ''}`).then((r) => r.templates),
  createShiftTemplate: (input: ShiftTemplateInput) =>
    api<{ template: ShiftTemplate }>('/api/hr/schedule/templates', { method: 'POST', body: input }).then((r) => r.template),
  updateShiftTemplate: (id: string, patch: Partial<ShiftTemplateInput>) =>
    api<{ template: ShiftTemplate }>(`/api/hr/schedule/templates/${id}`, { method: 'PATCH', body: patch }).then((r) => r.template),
  deleteShiftTemplate: (id: string) =>
    api<{ soft: boolean }>(`/api/hr/schedule/templates/${id}`, { method: 'DELETE' }),

  // Shift scheduling — assignments
  listShiftAssignments: (employeeId?: string) =>
    api<{ assignments: ShiftAssignment[] }>(`/api/hr/schedule/assignments${employeeId ? `?employee_id=${employeeId}` : ''}`).then((r) => r.assignments),
  createShiftAssignment: (input: CreateShiftAssignmentInput) =>
    api<{ assignment: ShiftAssignment }>('/api/hr/schedule/assignments', { method: 'POST', body: input }).then((r) => r.assignment),
  deleteShiftAssignment: (id: string) =>
    api<void>(`/api/hr/schedule/assignments/${id}`, { method: 'DELETE' }),

  // Shift scheduling — overrides
  listShiftOverrides: (opts: { from: string; to: string; employeeId?: string }) => {
    const qs = new URLSearchParams({ from: opts.from, to: opts.to });
    if (opts.employeeId) qs.set('employee_id', opts.employeeId);
    return api<{ overrides: ShiftOverride[] }>(`/api/hr/schedule/overrides?${qs}`).then((r) => r.overrides);
  },
  upsertShiftOverride: (input: UpsertShiftOverrideInput) =>
    api<{ override: ShiftOverride }>('/api/hr/schedule/overrides', { method: 'PUT', body: input }).then((r) => r.override),
  deleteShiftOverride: (id: string) =>
    api<void>(`/api/hr/schedule/overrides/${id}`, { method: 'DELETE' }),

  // Shift scheduling — week views (unwrapped response bodies, no wrapper key)
  getWeekSchedule: (start?: string) =>
    api<WeekSchedule>(`/api/hr/schedule/week${start ? `?start=${start}` : ''}`),
  getMySchedule: (start?: string) =>
    api<MySchedule>(`/api/hr/schedule/me${start ? `?start=${start}` : ''}`),

  // Performance — goals (manage/view_all)
  listGoals: (employeeId?: string) =>
    api<{ goals: PerformanceGoal[] }>(`/api/hr/performance/goals${employeeId ? `?employee_id=${employeeId}` : ''}`).then((r) => r.goals),
  createGoal: (input: CreateGoalInput) =>
    api<{ goal: PerformanceGoal }>('/api/hr/performance/goals', { method: 'POST', body: input }).then((r) => r.goal),
  updateGoal: (id: string, patch: UpdateGoalInput) =>
    api<{ goal: PerformanceGoal }>(`/api/hr/performance/goals/${id}`, { method: 'PATCH', body: patch }).then((r) => r.goal),
  deleteGoal: (id: string) =>
    api<void>(`/api/hr/performance/goals/${id}`, { method: 'DELETE' }),

  // Performance — reviews (manage/view_all)
  listReviews: (employeeId?: string) =>
    api<{ reviews: PerformanceReview[] }>(`/api/hr/performance/reviews${employeeId ? `?employee_id=${employeeId}` : ''}`).then((r) => r.reviews),
  createReviewDraft: (input: CreateReviewInput) =>
    api<{ review: PerformanceReview }>('/api/hr/performance/reviews', { method: 'POST', body: input }).then((r) => r.review),
  patchReviewDraft: (id: string, patch: PatchReviewInput) =>
    api<{ review: PerformanceReview }>(`/api/hr/performance/reviews/${id}`, { method: 'PATCH', body: patch }).then((r) => r.review),
  deleteReviewDraft: (id: string) =>
    api<void>(`/api/hr/performance/reviews/${id}`, { method: 'DELETE' }),
  shareReview: (id: string) =>
    api<{ review: PerformanceReview }>(`/api/hr/performance/reviews/${id}/share`, { method: 'POST', body: {} }).then((r) => r.review),

  // Performance — self ("My performance")
  myGoals: () =>
    api<{ goals: PerformanceGoal[] }>('/api/hr/performance/me/goals').then((r) => r.goals),
  myReviews: () =>
    api<{ reviews: PerformanceReview[] }>('/api/hr/performance/me/reviews').then((r) => r.reviews),
  acknowledgeReview: (id: string) =>
    api<{ review: PerformanceReview }>(`/api/hr/performance/me/reviews/${id}/acknowledge`, { method: 'POST', body: {} }).then((r) => r.review),
};
